/**
 * Realtor contacts — the referral partners an MLO markets rates to (the Realtor Marketing
 * email). Each contact belongs to ONE MLO; other MLOs never see it (admins and super admins
 * see every MLO's). Opt-outs are honoured company-wide: once an address unsubscribes it is
 * left out of every MLO's realtor send.
 *
 * Shared between the API (validation, import, export) and the web (CSV parsing, previews).
 */
import { z } from 'zod';
import { isValidEmail } from './util/email.js';

// ---------------------------------------------------------------------------
// US states
// ---------------------------------------------------------------------------

export const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' }, { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }, { code: 'PR', name: 'Puerto Rico' },
];
const STATE_BY_CODE = new Map(US_STATES.map((s) => [s.code, s.name]));
const STATE_BY_NAME = new Map(US_STATES.map((s) => [s.name.toUpperCase(), s.code]));

/** "tx" / "Texas" / "Texas (TX)" → "TX"; null when it isn't a US state. */
export function normalizeStateCode(input: unknown): string | null {
  const s = String(input ?? '').trim();
  if (!s) return null;
  const paren = s.match(/\(([A-Za-z]{2})\)/);
  if (paren && STATE_BY_CODE.has(paren[1]!.toUpperCase())) return paren[1]!.toUpperCase();
  if (/^[A-Za-z]{2}\.?$/.test(s) && STATE_BY_CODE.has(s.replace('.', '').toUpperCase())) return s.replace('.', '').toUpperCase();
  return STATE_BY_NAME.get(s.toUpperCase()) ?? null;
}
export const stateName = (code: string): string => STATE_BY_CODE.get(code) ?? code;

// ---------------------------------------------------------------------------
// Normalisers + schemas
// ---------------------------------------------------------------------------

export const normalizeEmail = (v: unknown): string => String(v ?? '').trim().toLowerCase();
/** Digits only → "(214) 555-0100"; other shapes are kept as typed (trimmed). */
export function normalizePhone(v: unknown): string | null {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const digits = s.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return s.slice(0, 40);
}

export const StateCodeSchema = z
  .string()
  .trim()
  .transform((s, ctx) => {
    const code = normalizeStateCode(s);
    if (!code) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `"${s}" is not a US state` });
      return z.NEVER;
    }
    return code;
  });

export const RealtorContactInputSchema = z.object({
  fullName: z.string().trim().min(1, 'Full name is required').max(120),
  email: z
    .string()
    .trim()
    .max(254)
    .transform((s) => s.toLowerCase())
    .refine((s) => isValidEmail(s), { message: 'Enter a valid email address' }),
  phone: z.string().trim().max(40).optional().or(z.literal('')).transform((s) => (s ? normalizePhone(s) : null)),
  state: StateCodeSchema,
  company: z.string().trim().max(120).optional().or(z.literal('')).transform((s) => s || null),
  notes: z.string().trim().max(500).optional().or(z.literal('')).transform((s) => s || null),
});
export type RealtorContactInput = z.infer<typeof RealtorContactInputSchema>;

export const MAX_IMPORT_ROWS = 5000;
export const RealtorContactImportSchema = z.object({
  rows: z.array(RealtorContactInputSchema).min(1).max(MAX_IMPORT_ROWS),
  /** 'upsert' updates contacts already in the list with the file's details; 'skip_existing' leaves them as they are. */
  mode: z.enum(['upsert', 'skip_existing']).default('upsert'),
  /** Admins only: the MLO whose list receives the contacts (default: the caller's own). */
  userId: z.string().min(1).max(60).optional(),
});

export interface RealtorContactDto {
  id: string;
  userId: string;
  /** The owning MLO (admins see every MLO's contacts). */
  ownerName: string | null;
  ownerEmail: string | null;
  fullName: string;
  email: string;
  phone: string | null;
  state: string;
  company: string | null;
  notes: string | null;
  /** ISO — set when the realtor unsubscribed (or was marked opted out); such a contact is never emailed. */
  optedOutAt: string | null;
  /** The address is on the company-wide suppression list (unsubscribed through any MLO's email). */
  suppressed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RealtorContactsSummary {
  /** Per state: contacts, and how many of them are opted out / suppressed (never emailed). */
  states: Array<{ state: string; total: number; optedOut: number; sendable: number }>;
  total: number;
  sendable: number;
}

// ---------------------------------------------------------------------------
// CSV (import / export)
// ---------------------------------------------------------------------------

/** RFC-4180-ish parser: quotes, doubled quotes, CR/LF, and comma / semicolon / tab delimiters (auto). */
export function parseCsv(text: string): string[][] {
  const src = text.replace(/^﻿/, '');
  const firstLine = src.split(/\r?\n/, 1)[0] ?? '';
  const delimiter = [',', '\t', ';'].map((d) => ({ d, n: (firstLine.match(new RegExp(d === '\t' ? '\t' : `\\${d}`, 'g')) ?? []).length })).sort((a, b) => b.n - a.n)[0]!.d;
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (quoted) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"') {
      quoted = true;
    } else if (ch === delimiter) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell.length || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

export type ContactColumn = 'fullName' | 'firstName' | 'lastName' | 'email' | 'phone' | 'state' | 'company' | 'notes';
export const CONTACT_COLUMN_LABELS: Record<ContactColumn, string> = {
  fullName: 'Full name',
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone',
  state: 'State',
  company: 'Company / brokerage',
  notes: 'Notes',
};

/** Guess which contact field a CSV header holds ("E-mail Address" → email, "St" → state …). */
export function guessContactColumn(header: string): ContactColumn | null {
  const h = header.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  if (!h) return null;
  if (/^(e ?mail|email address|e mail address|emailaddress|primary email)$/.test(h) || /\bemail\b/.test(h)) return 'email';
  if (/^(full ?name|name|contact|contact name|realtor|realtor name|agent|agent name)$/.test(h)) return 'fullName';
  if (/^(first|first name|given name|fname)$/.test(h)) return 'firstName';
  if (/^(last|last name|surname|family name|lname)$/.test(h)) return 'lastName';
  if (/\b(phone|mobile|cell|tel|telephone)\b/.test(h)) return 'phone';
  if (/^(state|st|state code|province|region|market state)$/.test(h)) return 'state';
  if (/\b(company|brokerage|broker|office|firm|team)\b/.test(h)) return 'company';
  if (/\b(notes?|comments?|remarks?)\b/.test(h)) return 'notes';
  return null;
}

export interface ContactCsvMapping {
  /** Column index per contact field (−1 = not in the file). */
  columns: Partial<Record<ContactColumn, number>>;
  hasHeader: boolean;
}

/** Map a parsed CSV to the contact fields: header row detection + column guesses (the user can fix them). */
export function guessContactMapping(rows: string[][]): ContactCsvMapping {
  const first = rows[0] ?? [];
  const guesses = first.map((h) => guessContactColumn(h));
  const hasHeader = guesses.some((g) => g === 'email') || guesses.filter(Boolean).length >= 2;
  const columns: Partial<Record<ContactColumn, number>> = {};
  if (hasHeader) {
    guesses.forEach((g, i) => {
      if (g && columns[g] == null) columns[g] = i;
    });
  } else {
    // No header: find the email column by content, then assume name before it and state / phone after.
    const sample = rows.slice(0, 20);
    const emailCol = first.findIndex((_, i) => sample.some((r) => isValidEmail(normalizeEmail(r[i]))));
    if (emailCol >= 0) columns.email = emailCol;
    const stateCol = first.findIndex((_, i) => i !== emailCol && sample.filter((r) => r[i]?.trim()).every((r) => normalizeStateCode(r[i])));
    if (stateCol >= 0) columns.state = stateCol;
    const phoneCol = first.findIndex((_, i) => i !== emailCol && i !== stateCol && sample.some((r) => (r[i] ?? '').replace(/\D/g, '').length >= 10));
    if (phoneCol >= 0) columns.phone = phoneCol;
    const nameCol = first.findIndex((_, i) => i !== emailCol && i !== stateCol && i !== phoneCol && sample.some((r) => /[A-Za-z]{2,}/.test(r[i] ?? '')));
    if (nameCol >= 0) columns.fullName = nameCol;
  }
  return { columns, hasHeader };
}

export interface MappedContactRow {
  line: number;
  input: { fullName: string; email: string; phone: string; state: string; company: string; notes: string };
  /** Problems that keep the row out of the import. */
  errors: string[];
}

/** Apply a mapping to the CSV rows: one candidate per data row with its validation problems. */
export function mapContactRows(rows: string[][], mapping: ContactCsvMapping): MappedContactRow[] {
  const data = mapping.hasHeader ? rows.slice(1) : rows;
  const get = (r: string[], c: ContactColumn) => (mapping.columns[c] != null && mapping.columns[c]! >= 0 ? (r[mapping.columns[c]!] ?? '').trim() : '');
  const seen = new Set<string>();
  return data.map((r, i) => {
    const first = get(r, 'firstName');
    const last = get(r, 'lastName');
    const fullName = get(r, 'fullName') || [first, last].filter(Boolean).join(' ');
    const email = normalizeEmail(get(r, 'email'));
    const stateRaw = get(r, 'state');
    const state = normalizeStateCode(stateRaw) ?? '';
    const errors: string[] = [];
    if (!fullName) errors.push('no name');
    if (!email) errors.push('no email');
    else if (!isValidEmail(email)) errors.push(`invalid email "${email}"`);
    if (!stateRaw) errors.push('no state');
    else if (!state) errors.push(`unknown state "${stateRaw}"`);
    if (email && seen.has(email)) errors.push('duplicate email in the file');
    if (email) seen.add(email);
    return { line: i + (mapping.hasHeader ? 2 : 1), input: { fullName, email, phone: get(r, 'phone'), state: state || stateRaw, company: get(r, 'company'), notes: get(r, 'notes') }, errors };
  });
}

const csvCell = (v: unknown): string => {
  const s = String(v ?? '');
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
/** Rows → CSV text (with a header row). */
export function toCsv(header: string[], rows: Array<Array<unknown>>): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
export const CONTACT_CSV_TEMPLATE = toCsv(['Full name', 'Email', 'Phone', 'State', 'Company'], [['Jane Realtor', 'jane@example.com', '(214) 555-0100', 'TX', 'Example Realty']]);
